import { ACTION_ROLES } from '../experience/experience';

/**
 * The day's jobs (PRD-MVP H3, UX-01): each is one click from Today, and Today is where everyone lands. A job is offered only when the caller's own menu has its screen (`screen`, a nav test id from
 * GET /api/platform/experience), so this list adds no permission of its own: the server's menu decides, and the API
 * still refuses by role whatever is drawn.
 *
 * `action` names a server action key (GET /api/platform/experience `actions`): a screen can be on a person's menu while
 * one of its tabs is not for them (staff open CK operations but only managers have "Send to a unit").
 * `roles` narrows a job to the people it is for when two jobs share a screen (a driver's "Deliveries" and site
 * staff's "Receive a transfer" are both My receipts). A job with `count` is a line of Today's "Needs your decision"
 * card, worded `row`, and `count` names what terminal/waiting.js reads for it. The rest are tiles.
 */
export const JOBS = [
  { id: 'approve-invoices', label: 'Approve invoices', row: 'To approve', hint: 'Supplier invoices waiting for you', to: '/review',
    screen: 'nav-cut-cost-review', count: 'awaiting_approval' },
  { id: 'disputed-invoices', label: 'Disputed invoices', row: 'Disputed', hint: 'Waiting on a supplier to correct or credit them',
    to: '/invoices?tab=disputed', screen: 'nav-invoices', roles: ['manager', 'org_owner', 'org_admin', 'accountant'],
    count: 'disputed' },
  // Lots need someone who can act on them, so these follow the Lots tab's own gate. No money in either.
  { id: 'use-soon', label: 'Use soon', row: 'Use soon', hint: 'Lots reaching their use-by within 7 days', to: '/ck-ops?tab=lots',
    screen: 'nav-cut-cost-ck-ops', action: 'ck.operate', count: 'use_soon' },
  { id: 'on-hold', label: 'On hold', row: 'On hold', hint: 'Lots held back from use', to: '/ck-ops?tab=lots',
    screen: 'nav-cut-cost-ck-ops', action: 'ck.operate', count: 'on_hold' },
  // The accountant reads (G-AUTH-1): the invoice list with its filters and export, and the delivery billing export.
  { id: 'read-invoices', label: 'Invoices and credit notes', hint: 'Filter them and export the list', to: '/invoices',
    screen: 'nav-invoices', roles: ['accountant'] },
  { id: 'billing-export', label: 'Delivery billing', hint: 'Charge sheets and the billing export', to: '/ck-ops/billing',
    screen: 'nav-cut-cost-ck-billing', roles: ['accountant'] },
  { id: 'scan-invoice', label: 'Scan invoice pages', hint: 'Photograph each page of one invoice', to: '/invoices?scan=1',
    screen: 'nav-invoices', roles: ['staff', 'manager', 'org_owner', 'org_admin'] },
  { id: 'upload-invoice', label: 'Upload invoice', hint: 'A PDF or photo from this device', to: '/invoices',
    screen: 'nav-invoices', roles: ['staff', 'manager', 'org_owner', 'org_admin'] },
  { id: 'receive-delivery', label: 'Receive a delivery', hint: 'A supplier van at the door', to: '/m/today',
    screen: 'nav-receiving-queue' },
  { id: 'use-stock', label: 'Use stock', hint: 'Take stock from a lot', to: '/use-stock', screen: 'nav-use-stock' },
  { id: 'prepare-food', label: 'Prepare food', hint: 'What you used and what you made', to: '/prepare-food', screen: 'nav-prepare-food', action: 'ck.produce' },
  { id: 'orders', label: 'Orders', hint: 'Raise an order to the kitchen, or prepare the units\u2019 orders',
    to: '/ck-ops?tab=orders', screen: 'nav-cut-cost-ck-ops', action: 'ck.operate' },
  { id: 'send', label: 'Send to a unit', hint: 'Dispatch prepared stock with its delivery note', to: '/ck-ops?tab=send',
    screen: 'nav-cut-cost-ck-ops', action: 'ck.manage' },
  { id: 'labels', label: 'Print pack labels', hint: 'Name, prep, use by, allergens and the lot code', to: '/ck-ops?tab=lots',
    screen: 'nav-cut-cost-ck-ops', action: 'ck.operate' },
  { id: 'receive-transfer', label: 'Receive a transfer', hint: 'Stock arriving from the kitchen', to: '/m/inbox',
    screen: 'nav-receiving-inbox', roles: ['staff', 'manager', 'org_owner', 'org_admin'] },
  { id: 'deliveries', label: 'Deliveries', hint: 'Today\u2019s drops and their delivery notes', to: '/m/inbox',
    screen: 'nav-receiving-inbox', roles: ['driver'] },
  { id: 'usage-waste', label: 'Record usage or waste', hint: 'Stock used in the kitchen, or thrown away', to: '/waste',
    screen: 'nav-cut-cost-waste' },
  // Who counts is the organisation's policy (G-STK-2): the server's `inventory.count` answer decides, not a role list.
  { id: 'count', label: 'Count stock', hint: 'One sheet per site', to: '/count',
    screen: 'nav-cut-cost-count', action: 'inventory.count' },
  { id: 'food-trace', label: 'Food trace', hint: 'Where a lot came from and where it went', to: '/food-trace', screen: 'nav-food-trace' },
  { id: 'alerts', label: 'Alerts', hint: 'Anything that needs a look', to: '/alerts', screen: 'nav-stay-compliant-alerts' },
];

/** Whether the server allows this action for the caller: its `actions` answer, else the roles that answer is built from. */
function actionAllowed(exp, key) {
  if (typeof exp?.actions?.[key] === 'boolean') return exp.actions[key];
  const allowed = ACTION_ROLES[key];
  return allowed ? (exp?.roles || []).some((r) => allowed.includes(r)) : true;
}

/** The jobs this caller may start: their menu has the screen, the job is for one of their roles, and the server
 * allows the action its tab needs. */
export function jobsFor(exp) {
  const nav = exp?.nav || new Set();
  const roles = exp?.roles || [];
  return JOBS.filter((job) => nav.has(job.screen)
    && (!job.roles || job.roles.some((r) => roles.includes(r)))
    && (!job.action || actionAllowed(exp, job.action)));
}

/** The people who work from the operations terminal (no prices, no settings): staff and drivers. */
export const TERMINAL_ROLES = ['staff', 'driver'];
export function isTerminalUser(exp) {
  const roles = exp?.roles || [];
  return roles.length > 0 && roles.every((r) => TERMINAL_ROLES.includes(r));
}
